import { AUTH_SIGNUP_URL } from '@/config/auth'
import { Header } from '@/shared/components/Header'
import { PlanSelector } from '@/shared/components/PlanSelector'
import { Button } from '@/shared/components/ui/button'
import { Typography } from '@/shared/components/ui/typography'
import { useUserContext } from '@/shared/contexts/userContext'
import { useCreateSubscription } from '@/shared/hooks/data-hooks/subscriptions/useCreateSubscription'
import { useSubscriptionPlan } from '@/shared/hooks/data-hooks/subscriptions/useSubscriptionPlan'
import { useToast } from '@/shared/hooks/useToast'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

export default function Plans() {
  const navigate = useNavigate()
  const { user } = useUserContext()
  const { toast } = useToast()

  const subscriptionPlanId = user?.subscriptions?.[0]?.subscriptionPlan

  const { data: { data: subscriptionPlan } = {} } = useSubscriptionPlan({
    id: subscriptionPlanId || '',
    enabled: !!subscriptionPlanId,
  })

  const currentPlanId = subscriptionPlan?.type || null

  const { mutateAsync: createSubscription, isPending: isCreatingSubscription } =
    useCreateSubscription()

  useEffect(() => {
    document.title = 'Plans & Pricing | Mydra'
  }, [])

  const handleGoBack = () => {
    navigate(-1)
  }

  const handlePlanChange = async (planId: string) => {
    try {
      if (!user) {
        const redirectTo = encodeURIComponent(
          window.location.pathname + window.location.search
        )
        window.location.href = `${AUTH_SIGNUP_URL}?product=ai-business&redirectTo=${redirectTo}`

        return
      }
      const { data } = await createSubscription({
        subscriptionPlanType: planId,
      })

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to update plan',
        description:
          error.message || 'An unexpected error occurred. Please try again.',
      })
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <Header hideUserAuth hideLinks />

      <div className="container mx-auto px-8 py-16">
        <header className="mb-12 text-center">
          <Typography variant="h2" as="h1">
            Plans that scale with your journey
          </Typography>
          <Typography variant="body1">
            Start a new workspace for individual or team use.
          </Typography>
        </header>

        <main className="flex">
          <PlanSelector
            className="mx-auto"
            currentPlanId={currentPlanId}
            onPlanChange={handlePlanChange}
            isLoading={isCreatingSubscription}
          />
        </main>

        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={handleGoBack}
            className="text-black hover:text-gray-700"
          >
            Go Back
          </Button>
        </div>
      </div>
    </div>
  )
}
